import { makeVar, useReactiveVar } from '@apollo/client';
import { localStorageService } from '@/services/local-storage-service';
import { stopReadAloud } from '@/utils/audio-utils/read-aloud.ts';

export enum SoundTrack {
  DigitalButton = 'digital-button.mp3',
  FingerSnap = 'finger-snap.mp3',
  GlockenspielDing = 'glockenspiel-ding.mp3',
  NotificationSweep = 'notification-sweep.mp3',
  ReadAloudFemale = 'read-aloud-female',
  ReadAloudMale = 'read-aloud-male',
}

export const SOUND_TRACK_LABELS: Record<SoundTrack, string> = {
  [SoundTrack.DigitalButton]: 'Digital Button',
  [SoundTrack.FingerSnap]: 'Finger Snap',
  [SoundTrack.GlockenspielDing]: 'Glockenspiel Ding',
  [SoundTrack.NotificationSweep]: 'Notification Sweep',
  [SoundTrack.ReadAloudFemale]: 'Read Aloud (Female)',
  [SoundTrack.ReadAloudMale]: 'Read Aloud (Male)',
};

export const isReadAloudTrack = (
  track: SoundTrack,
): track is SoundTrack.ReadAloudFemale | SoundTrack.ReadAloudMale =>
  track === SoundTrack.ReadAloudFemale || track === SoundTrack.ReadAloudMale;

export const DEFAULT_SOUND_TRACK = SoundTrack.DigitalButton;
export const DEFAULT_SOUND_VOLUME = 1;

export interface HighlightSoundSettings {
  enabled: boolean;
  track: SoundTrack;
  volume: number;
}

export interface CollectionSoundSettings {
  track: SoundTrack;
  volume: number;
  highlightSound: HighlightSoundSettings;
  onlyHighlights: boolean;
}

export type CollectionSoundSettingsMap = Record<
  string,
  CollectionSoundSettings
>;

const SOUND_SETTINGS_KEY = 'collection-sound-settings';

export const collectionSoundSettingsVar = makeVar<CollectionSoundSettingsMap>(
  {},
);

const initializeSoundSettings = () => {
  try {
    const stored = localStorageService.getItem(SOUND_SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as CollectionSoundSettingsMap;
      collectionSoundSettingsVar(parsed);
    }
  } catch {
    // Invalid JSON, ignore
  }
};

initializeSoundSettings();

export const getCollectionSoundSettings = (
  collectionId: string,
): CollectionSoundSettings => {
  const currentMap = collectionSoundSettingsVar();

  if (!(collectionId in currentMap)) {
    return {
      track: DEFAULT_SOUND_TRACK,
      volume: DEFAULT_SOUND_VOLUME,
      highlightSound: {
        enabled: false,
        track: SoundTrack.FingerSnap,
        volume: DEFAULT_SOUND_VOLUME,
      },
      onlyHighlights: false,
    };
  }

  const stored = currentMap[collectionId];

  // Ensure highlightSound has all required properties (for backward compatibility)
  return {
    track: stored.track || DEFAULT_SOUND_TRACK,
    volume: stored.volume ?? DEFAULT_SOUND_VOLUME,
    highlightSound: {
      enabled: stored.highlightSound?.enabled ?? false,
      track: stored.highlightSound?.track || SoundTrack.FingerSnap,
      volume: stored.highlightSound?.volume ?? DEFAULT_SOUND_VOLUME,
    },
    onlyHighlights: stored.onlyHighlights ?? false,
  };
};

export const setCollectionSoundSettings = (
  collectionId: string,
  settings: CollectionSoundSettings,
) => {
  const currentMap = collectionSoundSettingsVar();
  const updatedMap = { ...currentMap, [collectionId]: settings };

  collectionSoundSettingsVar(updatedMap);
  localStorageService.setItem(SOUND_SETTINGS_KEY, JSON.stringify(updatedMap));
};

export const setCollectionSoundTrack = (
  collectionId: string,
  track: SoundTrack,
) => {
  const current = getCollectionSoundSettings(collectionId);
  if (isReadAloudTrack(current.track) && !isReadAloudTrack(track)) {
    stopReadAloud(collectionId);
  }
  setCollectionSoundSettings(collectionId, { ...current, track });
};

export const setCollectionSoundVolume = (
  collectionId: string,
  volume: number,
) => {
  const current = getCollectionSoundSettings(collectionId);
  setCollectionSoundSettings(collectionId, { ...current, volume });
};

export const setHighlightSoundEnabled = (
  collectionId: string,
  enabled: boolean,
) => {
  const current = getCollectionSoundSettings(collectionId);

  // When enabling, ensure track and volume are set with defaults if they don't exist
  const highlightSound = enabled
    ? {
        enabled: true,
        track: current.highlightSound?.track || SoundTrack.FingerSnap,
        volume: current.highlightSound?.volume ?? DEFAULT_SOUND_VOLUME,
      }
    : {
        ...current.highlightSound,
        enabled: false,
      };

  setCollectionSoundSettings(collectionId, {
    ...current,
    highlightSound,
  });
};

export const setHighlightSoundTrack = (
  collectionId: string,
  track: SoundTrack,
) => {
  const current = getCollectionSoundSettings(collectionId);
  if (
    current.highlightSound?.track &&
    isReadAloudTrack(current.highlightSound.track) &&
    !isReadAloudTrack(track)
  ) {
    stopReadAloud(collectionId);
  }

  // Ensure highlightSound object exists with proper defaults
  const highlightSound = {
    enabled: current.highlightSound?.enabled ?? false,
    track: track,
    volume: current.highlightSound?.volume ?? DEFAULT_SOUND_VOLUME,
  };

  setCollectionSoundSettings(collectionId, {
    ...current,
    highlightSound,
  });
};

export const setHighlightSoundVolume = (
  collectionId: string,
  volume: number,
) => {
  const current = getCollectionSoundSettings(collectionId);

  // Ensure highlightSound object exists with proper defaults
  const highlightSound = {
    enabled: current.highlightSound?.enabled ?? false,
    track: current.highlightSound?.track || SoundTrack.FingerSnap,
    volume: volume,
  };

  setCollectionSoundSettings(collectionId, {
    ...current,
    highlightSound,
  });
};

export const setOnlyHighlightsSound = (
  collectionId: string,
  onlyHighlights: boolean,
) => {
  const current = getCollectionSoundSettings(collectionId);
  setCollectionSoundSettings(collectionId, { ...current, onlyHighlights });
};

export const hasAnySoundEnabled = (
  settings: CollectionSoundSettings,
): boolean => {
  const hasGeneralSound = !settings.onlyHighlights;
  const hasHighlightSound = settings.highlightSound.enabled;
  return hasGeneralSound || hasHighlightSound;
};

export const useCollectionSoundSettings = (
  collectionId: string,
): CollectionSoundSettings => {
  useReactiveVar(collectionSoundSettingsVar);
  return getCollectionSoundSettings(collectionId);
};
